import {
  TenantConfig,
  TenantPolicies,
  TenantRefundPolicy,
  TenantRulesPolicy,
} from './tenant.model';
import { safeSupportEmail, supportMailto } from './tenant-support-email';

export const OFFICIAL_FIBA_RULES_URL = 'https://about.fiba.basketball/en/services/resource-hub/downloads';
export const POLICY_LIMITS = Object.freeze({
  bytes: 16 * 1024,
  introduction: 2000,
  sections: 16,
  heading: 120,
  body: 3000,
  items: 12,
  itemText: 750,
  subitems: 6,
  subitem: 750,
});

export type TenantPolicyMode = 'configured' | 'unavailable';

export interface TenantPolicyView<T extends TenantRulesPolicy | TenantRefundPolicy> {
  mode: TenantPolicyMode;
  document: T;
  unavailableMessage: string;
  supportEmail: string;
  supportMailto: string;
}

function compact(value: unknown): string {
  return String(value ?? '').trim();
}

export function emptyRulesPolicy(configured = false): TenantRulesPolicy {
  return { configured, introduction: '', show_fiba_resource: false, sections: [] };
}

export function emptyRefundPolicy(configured = false): TenantRefundPolicy {
  return { configured, introduction: '', sections: [] };
}

export function emptyTenantPolicies(): TenantPolicies {
  return { rules: emptyRulesPolicy(), refund: emptyRefundPolicy() };
}

export function hasRulesPolicyContent(policy: TenantRulesPolicy): boolean {
  return Boolean(compact(policy.introduction) || policy.show_fiba_resource === true || policy.sections?.length);
}

export function hasRefundPolicyContent(policy: TenantRefundPolicy): boolean {
  return Boolean(compact(policy.introduction) || policy.sections?.length);
}

export function cloneRulesPolicy(policy: TenantRulesPolicy): TenantRulesPolicy {
  return structuredClone(policy);
}

export function cloneRefundPolicy(policy: TenantRefundPolicy): TenantRefundPolicy {
  return structuredClone(policy);
}

function safeRulesPolicy(value: TenantRulesPolicy | undefined): TenantRulesPolicy {
  return value && Array.isArray(value.sections) ? cloneRulesPolicy(value) : emptyRulesPolicy();
}

function safeRefundPolicy(value: TenantRefundPolicy | undefined): TenantRefundPolicy {
  return value && Array.isArray(value.sections) ? cloneRefundPolicy(value) : emptyRefundPolicy();
}

export function resolveRulesPolicy(tenant: TenantConfig | null, leagueName: string): TenantPolicyView<TenantRulesPolicy> {
  const configured = safeRulesPolicy(tenant?.policies?.rules);
  const supportEmail = safeSupportEmail(tenant?.contact?.support_email);
  const safeMailto = supportMailto(supportEmail);
  const unavailableMessage = `Rules and regulations have not been published for ${leagueName}. Contact ${supportEmail || 'league administration'} for current information.`;
  if (configured.configured === true) {
    return { mode: hasRulesPolicyContent(configured) ? 'configured' : 'unavailable', document: configured, unavailableMessage, supportEmail, supportMailto: safeMailto };
  }
  return { mode: 'unavailable', document: emptyRulesPolicy(), unavailableMessage, supportEmail, supportMailto: safeMailto };
}

export function resolveRefundPolicy(tenant: TenantConfig | null, leagueName: string): TenantPolicyView<TenantRefundPolicy> {
  const configured = safeRefundPolicy(tenant?.policies?.refund);
  const supportEmail = safeSupportEmail(tenant?.contact?.support_email);
  const safeMailto = supportMailto(supportEmail);
  const unavailableMessage = `The refund policy has not been published for ${leagueName}. Contact ${supportEmail || 'league administration'} for current information.`;
  if (configured.configured === true) {
    return { mode: hasRefundPolicyContent(configured) ? 'configured' : 'unavailable', document: configured, unavailableMessage, supportEmail, supportMailto: safeMailto };
  }
  return { mode: 'unavailable', document: emptyRefundPolicy(), unavailableMessage, supportEmail, supportMailto: safeMailto };
}
